import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import type { ApiClientError } from "./_errors";
import { apiJson } from "./_http";
import type {
	CastVoteRequest,
	CreateIdeaRequest,
	Idea,
	IdeaWithConsensus,
	ListIdeasResponse,
} from "../types";

// TanStack Query hooks for the vote service. All HTTP traffic flows through
// the web-app's own Pages Functions at /api/* — never direct to the
// services-cf Workers — so the session cookie verification stays
// server-side.

// --- Query keys --------------------------------------------------------------

export const ideaKeys = {
	all: ["ideas"] as const,
	detail: (id: string) => [...ideaKeys.all, "detail", id] as const,
	listByFam: (famId: string) => [...ideaKeys.all, "fam", famId] as const,
};

// --- Hooks -------------------------------------------------------------------

// useIdea fetches a single idea + live consensus tally. Polls every 5s so the
// vote count refreshes without WebSockets. Stops polling once the idea is
// resolved (status !== "open"/"pm_unilateral"-pending isn't a thing — PM
// unilaterals resolve at create-time too).
export function useIdea(id: string | undefined): UseQueryResult<IdeaWithConsensus> {
	return useQuery({
		queryKey: ideaKeys.detail(id ?? "missing"),
		queryFn: () => {
			if (!id) throw new Error("missing idea id");
			return apiJson<IdeaWithConsensus>(`/api/ideas/${encodeURIComponent(id)}`);
		},
		enabled: !!id,
		refetchInterval: (query) => {
			const data = query.state.data;
			if (!data) return 5_000;
			// Stop polling once the idea is no longer accepting votes.
			return data.idea.status === "open" ? 5_000 : false;
		},
		staleTime: 0,
	});
}

// useIdeasList lists ideas in a fam, defaulting to status=open. Useful for
// rehydrating the Fam viewer's open-ideas section after a propose/withdraw
// invalidation when the fam endpoint doesn't (yet) inline openIdeas.
export function useIdeasList(
	famSlugOrId: string | undefined,
	status = "open",
): UseQueryResult<ListIdeasResponse> {
	return useQuery({
		queryKey: [...ideaKeys.listByFam(famSlugOrId ?? "missing"), status],
		queryFn: () => {
			if (!famSlugOrId) throw new Error("missing fam id");
			const qs = status ? `?status=${encodeURIComponent(status)}` : "";
			return apiJson<ListIdeasResponse>(
				`/api/fams/${encodeURIComponent(famSlugOrId)}/ideas${qs}`,
			);
		},
		enabled: !!famSlugOrId,
	});
}

// useCreateIdea POSTs a proposal. On success, invalidates the fam viewer's
// query (`["fam", slug]`) so the new idea shows up, plus the listByFam query.
export function useCreateIdea(
	famSlug: string,
): UseMutationResult<Idea, ApiClientError, CreateIdeaRequest> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: CreateIdeaRequest) =>
			apiJson<Idea>(`/api/fams/${encodeURIComponent(famSlug)}/ideas`, {
				method: "POST",
				body: JSON.stringify(body),
			}),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["fam", famSlug] });
			void qc.invalidateQueries({ queryKey: ideaKeys.listByFam(famSlug) });
		},
	});
}

// useCastVote POSTs a vote. Optimistically bumps the consensus tally for the
// idea's detail query — TanStack v5 idiom: snapshot in onMutate, restore in
// onError, refetch in onSettled.
export function useCastVote(
	famSlug: string,
	ideaId: string,
): UseMutationResult<
	void,
	ApiClientError,
	CastVoteRequest,
	{ previous: IdeaWithConsensus | undefined }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: CastVoteRequest) =>
			apiJson<void>(
				`/api/fams/${encodeURIComponent(famSlug)}/ideas/${encodeURIComponent(ideaId)}/votes`,
				{ method: "POST", body: JSON.stringify(body) },
			),
		onMutate: async (body) => {
			const key = ideaKeys.detail(ideaId);
			await qc.cancelQueries({ queryKey: key });
			const previous = qc.getQueryData<IdeaWithConsensus>(key);
			if (previous) {
				const next: IdeaWithConsensus = {
					...previous,
					consensus: {
						...previous.consensus,
						yes: previous.consensus.yes + (body.choice === "yes" ? 1 : 0),
						no: previous.consensus.no + (body.choice === "no" ? 1 : 0),
					},
				};
				qc.setQueryData(key, next);
			}
			return { previous };
		},
		onError: (_err, _body, ctx) => {
			if (ctx?.previous) qc.setQueryData(ideaKeys.detail(ideaId), ctx.previous);
		},
		onSettled: () => {
			void qc.invalidateQueries({ queryKey: ideaKeys.detail(ideaId) });
		},
	});
}

// useWithdrawIdea DELETEs an idea. Proposer-only at the server; the UI gates
// the button on idea.proposerUserId === currentUserId.
export function useWithdrawIdea(
	famSlug: string,
	ideaId: string,
): UseMutationResult<void, ApiClientError, void> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: () =>
			apiJson<void>(`/api/ideas/${encodeURIComponent(ideaId)}`, {
				method: "DELETE",
			}),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ideaKeys.detail(ideaId) });
			void qc.invalidateQueries({ queryKey: ["fam", famSlug] });
			void qc.invalidateQueries({ queryKey: ideaKeys.listByFam(famSlug) });
		},
	});
}
